/**
 * Double écran « Régie » (formule Pro) : l'animateur pilote depuis la régie
 * (`/animator/:clientID/regie`), l'écran public (page TV ouverte avec
 * `display=public`) n'affiche que ce que voit la salle.
 *
 * `screenInfos.anim.regie` est écrit par la régie, champ par champ
 * (ClientDatabase.setRegie), et lu en direct par l'écran public.
 * `screenInfos.anim.roster` est écrit par l'écran public pour la régie.
 */

/** Ce que montre l'écran public : l'accueil de la soirée, le jeu, ou un message plein écran. */
export type RegieView = 'lobby' | 'game' | 'message';

export const REGIE_VIEWS: RegieView[] = ['lobby', 'game', 'message'];

export type RegieState = {
    view: RegieView;
    /** Nom de la soirée sur l'accueil (vide → nom de l'établissement). */
    eventName: string;
    /** Bandeau défilant en bas de l'accueil. */
    ticker: string;
    tickerOn: boolean;
    /** Message plein écran (vue `message`). */
    message: string;
    /** Dernière écriture de la régie (ms) : un état vieux d'une soirée ne doit pas resurgir. */
    updatedAt?: number;
    /** Vue d'avant le message plein écran : « Retirer » y revient, depuis n'importe quelle régie. */
    prevView?: RegieView;
};

export type RosterPlayer = {
    id: string;
    name: string;
    avatar?: string;
    color?: string;
};

/** Bornes des textes : ils s'affichent en grand sur une TV et transitent par le doc client. */
export const REGIE_LIMITS = { eventName: 60, ticker: 200, message: 140 } as const;

/** Au-delà, la liste n'est plus lisible dans la régie et alourdit le doc client. */
export const ROSTER_MAX = 60;

const str = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : '');

/** Champ écrit par une page web : forme non garantie (vieille version, saisie à la main). */
export function parseRegie(data: unknown): RegieState | undefined {
    if (!data || typeof data !== 'object') return undefined;
    const d = data as Record<string, unknown>;
    return {
        view: REGIE_VIEWS.includes(d.view as RegieView) ? (d.view as RegieView) : 'game',
        eventName: str(d.eventName, REGIE_LIMITS.eventName),
        ticker: str(d.ticker, REGIE_LIMITS.ticker),
        tickerOn: d.tickerOn === true,
        message: str(d.message, REGIE_LIMITS.message),
        updatedAt: typeof d.updatedAt === 'number' ? d.updatedAt : undefined,
        prevView: d.prevView === 'lobby' || d.prevView === 'game' ? d.prevView : undefined,
    };
}

/** Au-delà, l'état de la régie date d'une autre soirée. */
export const REGIE_STALE_MS = 12 * 60 * 60 * 1000;

/**
 * Ce que l'écran public doit montrer : l'accueil par défaut (pas encore de régie, ou
 * un état d'une soirée précédente — un vieux « pause 5 min » ne doit pas resurgir).
 */
export function effectiveRegieView(regie: RegieState | undefined, now = Date.now()): RegieView {
    if (!regie || !regie.updatedAt || now - regie.updatedAt > REGIE_STALE_MS) return 'lobby';
    return regie.view;
}

/** Ce que l'écran public surveille dans la fiche de l'écran (cf regieReaction). */
export type RegieWatch = {
    game: string;
    /** Nonce de « Lancer » (requestLaunch). */
    launch: number;
    paused: boolean;
    /** Nonce de « Changer de jeu » (changeGame), même quand le jeu choisi est le même. */
    changed: number;
};

/**
 * Hors jeu (accueil, message), ce que l'écran public doit faire quand la fiche change :
 * - « Lancer » (régie, télécommande) → montrer le jeu ;
 * - « Reprendre » seul (téléphone qui ne sait pas qu'un accueil est affiché) → le jeu,
 *   sinon la partie filerait sous le calque ;
 * - partie qui tourne sous le calque (changement de jeu, qui retire la pause ; état
 *   d'une autre soirée à l'ouverture) → la remettre en pause, l'accueil reste.
 * `prev` null = montage : seules les valeurs actuelles comptent (pas d'événement).
 */
export function regieReaction(prev: RegieWatch | null, now: RegieWatch, view: RegieView): 'show-game' | 'pause' | null {
    if (view === 'game') return null;
    if (prev) {
        if (now.launch > prev.launch) return 'show-game'; // nonce qui ne fait que monter
        const gameChanged = now.changed !== prev.changed || now.game !== prev.game;
        if (!gameChanged && prev.paused && !now.paused) return 'show-game';
    }
    return now.paused ? null : 'pause';
}

/** Avatar venu du téléphone d'un joueur : une URL http(s) de taille raisonnable, sinon rien. */
export function safeAvatar(v: unknown): string | undefined {
    return typeof v === 'string' && v.length <= 300 && /^https?:\/\//.test(v) ? v : undefined;
}

export function parseRoster(data: unknown): RosterPlayer[] | undefined {
    if (!Array.isArray(data)) return undefined;
    return data
        .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object' && typeof p.id === 'string')
        .slice(0, ROSTER_MAX)
        .map((p) => ({
            id: p.id as string,
            name: str(p.name, 40) || '?',
            avatar: safeAvatar(p.avatar),
            color: typeof p.color === 'string' ? p.color : undefined,
        }));
}

/**
 * Ce que l'écran public dit aux téléphones (propriété partagée de la session, posée par
 * la TV) : hors jeu, ils affichent un écran d'attente au lieu de la manette du jeu.
 * Clé NOUVELLE (gotcha #232 : jamais changer la forme d'un message existant).
 */
export const REGIE_PHONE_KEY = 'regiePhone';

export type RegiePhoneState = {
    view: RegieView;
    /** Nom de la soirée, ou de l'établissement. */
    title: string;
    /** Message plein écran affiché sur la TV (vue `message`). */
    message: string;
    /** Bandeau de la TV, s'il est allumé. */
    ticker: string;
};

/** Venu du réseau : forme non garantie (TV sur une autre version). `undefined` = pas de régie. */
export function parseRegiePhone(data: unknown): RegiePhoneState | undefined {
    if (!data || typeof data !== 'object') return undefined;
    const d = data as Record<string, unknown>;
    if (!REGIE_VIEWS.includes(d.view as RegieView)) return undefined;
    return {
        view: d.view as RegieView,
        title: str(d.title, REGIE_LIMITS.eventName),
        message: str(d.message, REGIE_LIMITS.message),
        ticker: str(d.ticker, REGIE_LIMITS.ticker),
    };
}
